import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Button,
  Progress,
  Select,
  Upload,
  Calendar,
  Space,
  Avatar,
  Spin,
  message,
} from 'antd';
import {
  DollarOutlined,
  FileTextOutlined,
  FolderOutlined,
  CalendarOutlined,
  UploadOutlined,
  EyeOutlined,
  CheckOutlined,
  ReadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import customerApi from '../services/customerApi';
import { customerAuthService } from '../services/customerAuth';
import { useNavigate } from 'react-router-dom';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

interface DashboardData {
  quotation: {
    pending: number;
    amount: number;
  };
  invoice: {
    unpaid: number;
    outstanding: number;
  };
  ongoing_tasks: Array<{
    id: number;
    task_number: string;
    created_at: string;
    type: string;
    due_date: string;
    deadline: string;
    quotation: string;
    invoice: string;
    staff: string;
    task_name: string;
    year: number;
    status: string;
    status_percent: number;
    draft_report?: {
      id: number;
      name: string;
      path: string;
    };
  }>;
  ongoing_letters: Array<{
    id: number;
    letter_number: string;
    created_at: string;
    due_date: string;
    sender: string;
    subject: string;
    staff: string;
    status: string;
    status_percent: number;
    items: Array<{
      id: number;
      name: string;
      path: string;
    }>;
  }>;
  events: Array<{
    id: number;
    title: string;
    type: string;
    start: string;
    location?: string;
  }>;
  today_events: Array<{
    id: number;
    title: string;
    type: string;
    time: string;
  }>;
  staff_roles: Array<{
    id: number;
    name: string;
    initial: string;
    role: string;
  }>;
}

export const CustomerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<DashboardData | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const response = await customerApi.get('/customer/dashboard');
      setData(response.data.data);
    } catch (error: any) {
      console.error('Failed to load dashboard data:', error);
      message.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return `RM ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      // Task statuses
      Plan: '#faad14',
      Progress: '#fa8c16',
      Review: '#ff4d4f',
      Approve: '#52c41a',
      Completed: '#52c41a',
      // Letter statuses
      Unread: '#faad14',
      Read: '#52c41a',
      // Legacy statuses (for backward compatibility)
      draft: '#1890ff',
      sent: '#faad14',
    };
    return colors[status] || '#d9d9d9';
  };

  const getStatusLabel = (status: string) => {
    // Map old status values to new ones for letters
    if (status === 'draft') return 'Plan';
    if (status === 'sent') return 'Sent';
    if (status === 'Unread') return 'Sent';
    return status;
  };

  const handleTaskReview = (taskId: number) => {
    navigate(`/customer/tasks/${taskId}`);
  };

  const handleTaskApprove = async (taskId: number) => {
    try {
      await customerApi.post('/customer/task-reviews', {
        task_id: taskId,
        rating: 5,
        comment: 'Approved',
      });
      message.success('Task approved successfully');
      loadDashboardData();
    } catch (error: any) {
      message.error('Failed to approve task');
    }
  };

  const handleLetterRead = async (letterId: number) => {
    try {
      await customerApi.patch(`/customer/letters/${letterId}/read`);
      message.success('Letter marked as read');
      // Reload dashboard data
      loadDashboardData();
    } catch (error: any) {
      message.error('Failed to mark letter as read');
      // Still navigate to letter detail page even if API call fails
    navigate(`/customer/letters/${letterId}`);
    }
  };

  const handleLetterReview = (letterId: number) => {
    navigate(`/customer/letters/${letterId}`);
  };

  const handleViewDraftReport = async (report: any) => {
    try {
      const response = await customerApi.get(`/customer/task-draft-reports/${report.id}/view`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error: any) {
      message.error('Failed to view draft report');
    }
  };

  const handleViewLetterItem = async (item: any) => {
    try {
      const response = await customerApi.get(`/customer/letter-items/${item.id}/view`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error: any) {
      message.error('Failed to view letter item');
    }
  };

  const onCalendarSelect = (value: Dayjs) => {
    // Handle calendar date selection if needed
  };

  const onCalendarChange = (value: Dayjs) => {
    // Handle calendar change if needed
  };

  const getYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear; i >= currentYear - 10; i--) {
      years.push(i.toString());
    }
    return years;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 20px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 20px' }}>
        <Text>No data available</Text>
      </div>
    );
  }

  const ongoingTotal = data.ongoing_tasks.length + data.ongoing_letters.length;

  return (
    <div>
      <Title level={1} style={{ marginBottom: 24, fontSize: 24, fontWeight: 600 }}>
        Dashboard
      </Title>
      
      <Row gutter={[16, 16]}>
        {/* Quotation Widget */}
        <Col xs={24} sm={12} md={6}>
          <Card
            style={{
              background: '#fffbe6',
              border: 'none',
              borderRadius: 8,
              height: '100%',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
              <DollarOutlined style={{ fontSize: 24, color: '#faad14', marginRight: 8 }} />
              <Title level={5} style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
                Quotation
              </Title>
            </div>
            <div style={{ marginBottom: 8 }}>
              <Text style={{ fontSize: 14 }}>Pending</Text>
              <div style={{ fontSize: 20, fontWeight: 600, color: '#faad14' }}>
                {data.quotation.pending}
              </div>
            </div>
            <div>
              <Text style={{ fontSize: 14 }}>Amount</Text>
              <div style={{ fontSize: 18, fontWeight: 600, color: '#faad14' }}>
                {formatCurrency(data.quotation.amount)}
              </div>
            </div>
          </Card>
        </Col>

        {/* Invoice Widget */}
        <Col xs={24} sm={12} md={6}>
          <Card
            style={{
              background: '#fff0f6',
              border: 'none',
              borderRadius: 8,
              height: '100%',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
              <DollarOutlined style={{ fontSize: 24, color: '#eb2f96', marginRight: 8 }} />
              <Title level={5} style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
                Invoice
              </Title>
            </div>
            <div style={{ marginBottom: 8 }}>
              <Text style={{ fontSize: 14 }}>Unpaid</Text>
              <div style={{ fontSize: 20, fontWeight: 600, color: '#eb2f96' }}>
                {data.invoice.unpaid}
              </div>
            </div>
    <div>
              <Text style={{ fontSize: 14 }}>Outstanding</Text>
              <div style={{ fontSize: 18, fontWeight: 600, color: '#eb2f96' }}>
                {formatCurrency(data.invoice.outstanding)}
              </div>
            </div>
          </Card>
        </Col>

        {/* Shortcut to File Widget */}
        <Col xs={24} sm={12} md={6}>
          <Card
            style={{
              background: '#e6f7ff',
              border: 'none',
              borderRadius: 8,
              height: '100%',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
              <FolderOutlined style={{ fontSize: 24, color: '#1890ff', marginRight: 8 }} />
              <Title level={5} style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
                Shortcut to File
              </Title>
            </div>
            <div style={{ marginBottom: 8 }}>
              <Select
                placeholder="Select Customer"
                style={{ width: '100%', marginBottom: 8 }}
                value={selectedCustomer}
                onChange={setSelectedCustomer}
              >
                <Option value="">All Customers</Option>
              </Select>
              <Select
                placeholder="Select Year"
                style={{ width: '100%', marginBottom: 8 }}
                value={selectedYear}
                onChange={setSelectedYear}
              >
                {getYears().map((year) => (
                  <Option key={year} value={year}>
                    {year}
                  </Option>
                ))}
              </Select>
              <Select
                placeholder="Select Category"
                style={{ width: '100%', marginBottom: 8 }}
                value={selectedCategory}
                onChange={setSelectedCategory}
              >
                <Option value="files">Files</Option>
                <Option value="reports">Reports</Option>
              </Select>
            </div>
            <Button
              type="primary"
              icon={<UploadOutlined />}
              block
              onClick={() => {
                if (selectedCategory === 'files') {
                  navigate('/customer/files');
                } else if (selectedCategory === 'reports') {
                  navigate('/customer/reports');
                } else {
                  message.info('Please select a category first');
                }
              }}
            >
              Drag and drop files here to upload
            </Button>
          </Card>
        </Col>

        {/* Calendar Widget */}
        <Col xs={24} sm={12} md={6}>
          <Card
            style={{
              background: '#f9f0ff',
              border: 'none',
              borderRadius: 8,
              height: '100%',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
              <CalendarOutlined style={{ fontSize: 24, color: '#722ed1', marginRight: 8 }} />
              <Title level={5} style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
                Calendar
              </Title>
            </div>
            <Calendar
              fullscreen={false}
              onSelect={onCalendarSelect}
              onChange={onCalendarChange}
              style={{ background: 'transparent' }}
            />
            {data.today_events.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Text strong style={{ fontSize: 14 }}>
                  Event ({data.today_events.length})
                </Text>
                {data.today_events.map((event) => (
                  <div
                    key={event.id}
                    style={{
                      marginTop: 8,
                      padding: '8px 12px',
                      background: event.type === 'appointment' ? '#f6ffed' : '#fff7e6',
                      borderRadius: 4,
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 600 }}>
                      {event.title}
                    </div>
                    <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                      {event.time} - {event.type === 'appointment' ? 'Appointment' : 'Task'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </Col>

        {/* Ongoing Section */}
        <Col xs={24}>
          <Title level={2} style={{ fontSize: 20, fontWeight: 600, marginBottom: 16 }}>
            Ongoing ({ongoingTotal})
          </Title>
        </Col>

        {/* Ongoing Tasks */}
        {data.ongoing_tasks.map((task) => (
          <Col xs={24} key={`task-${task.id}`}>
            <Card
              style={{
                marginBottom: 16,
                borderRadius: 8,
                position: 'relative',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  fontSize: 12,
                  color: '#8c8c8c',
                }}
              >
                Created {task.created_at}
              </div>
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <div style={{ marginBottom: 8 }}>
                    <Text style={{ fontSize: 12, color: '#8c8c8c' }}>Type: </Text>
                    <Text strong>{task.type}</Text>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <Text style={{ fontSize: 12, color: '#8c8c8c' }}>Due: </Text>
                    <Text>{task.due_date}</Text>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <Text style={{ fontSize: 12, color: '#8c8c8c' }}>Quotation: </Text>
                    <Text>{task.quotation}</Text>
                  </div>
                  <div>
                    <Text style={{ fontSize: 12, color: '#8c8c8c' }}>Staff: </Text>
                    <Text>{task.staff}</Text>
                  </div>
                </Col>
                <Col xs={24} md={8}>
                  <div style={{ marginBottom: 8 }}>
                    <Text style={{ fontSize: 12, color: '#8c8c8c' }}>Task: </Text>
                    <Text strong>{task.task_name}</Text>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <Text style={{ fontSize: 12, color: '#8c8c8c' }}>Deadline: </Text>
                    <Text>{task.deadline}</Text>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <Text style={{ fontSize: 12, color: '#8c8c8c' }}>Invoice: </Text>
                    <Text>{task.invoice}</Text>
                  </div>
                  {task.draft_report && (
                    <div>
                      <Text style={{ fontSize: 12, color: '#8c8c8c' }}>Draft Report: </Text>
                      <Button
                        type="link"
                        icon={<FileTextOutlined />}
                        onClick={() => handleViewDraftReport(task.draft_report)}
                        style={{ padding: 0, height: 'auto' }}
                      >
                        {task.draft_report.name || 'projects.reports.update'}
                      </Button>
                    </div>
                  )}
                </Col>
                <Col xs={24} md={8}>
                  <div style={{ marginBottom: 8 }}>
                    <Text style={{ fontSize: 12, color: '#8c8c8c' }}>Year: </Text>
                    <Text>{task.year}</Text>
                  </div>
                  <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    <Progress
                      type="circle"
                      percent={task.status_percent}
                      format={() => task.status_percent + '%'}
                      strokeColor={getStatusColor(task.status)}
                      size={80}
                    />
                    <div style={{ marginTop: 8, fontSize: 12, fontWeight: 600, color: getStatusColor(task.status) }}>
                      {getStatusLabel(task.status)}
                    </div>
                  </div>
                  <Space>
                    <Button
                      size="small"
                      onClick={() => handleTaskReview(task.id)}
                    >
                      Customer Review
                    </Button>
                    {task.status === 'Approve' && (
                      <Button
                        type="primary"
                        size="small"
                        icon={<CheckOutlined />}
                        onClick={() => handleTaskApprove(task.id)}
                        style={{ background: '#52c41a', borderColor: '#52c41a' }}
                      >
                        Approve
                      </Button>
                    )}
                  </Space>
                </Col>
              </Row>
            </Card>
          </Col>
        ))}

        {/* Ongoing Letters */}
        {data.ongoing_letters.map((letter) => (
          <Col xs={24} key={`letter-${letter.id}`}>
            <Card
              style={{
                marginBottom: 16,
                borderRadius: 8,
                position: 'relative',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  fontSize: 12,
                  color: '#8c8c8c',
                }}
              >
                Created {letter.created_at}
              </div>
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <div style={{ marginBottom: 8 }}>
                    <Text style={{ fontSize: 12, color: '#8c8c8c' }}>Due: </Text>
                    <Text>{letter.due_date}</Text>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <Text style={{ fontSize: 12, color: '#8c8c8c' }}>Sender: </Text>
                    <Text>{letter.sender}</Text>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <Text style={{ fontSize: 12, color: '#8c8c8c' }}>Subject: </Text>
                    <Text>{letter.subject}</Text>
                  </div>
                  <div>
                    <Text style={{ fontSize: 12, color: '#8c8c8c' }}>Staff: </Text>
                    <Text>{letter.staff}</Text>
                  </div>
                </Col>
                <Col xs={24} md={8}>
                  <div style={{ marginBottom: 8 }}>
                    <Text style={{ fontSize: 12, color: '#8c8c8c' }}>Letters: </Text>
                    {letter.items && letter.items.length > 0 ? (
                      letter.items.map((item) => (
                        <Button
                          key={item.id}
                          type="link"
                          icon={<FileTextOutlined />}
                          onClick={() => handleViewLetterItem(item)}
                          style={{ padding: 0, height: 'auto', marginLeft: 4 }}
                        >
                          {item.name || 'projects.letters.update'}
                        </Button>
                      ))
                    ) : (
                      <Text>No items</Text>
                    )}
                  </div>
                </Col>
                <Col xs={24} md={8}>
                  <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    <Progress
                      type="circle"
                      percent={letter.status_percent}
                      format={() => letter.status_percent + '%'}
                      strokeColor={getStatusColor(letter.status)}
                      size={80}
                    />
                    <div style={{ marginTop: 8, fontSize: 12, fontWeight: 600, color: getStatusColor(letter.status) }}>
                      {getStatusLabel(letter.status)}
                    </div>
                  </div>
                  <Space>
                    <Button
                      size="small"
                      onClick={() => handleLetterReview(letter.id)}
                    >
                      Customer Review
                    </Button>
                    <Button
                      type="primary"
                      size="small"
                      icon={<ReadOutlined />}
                      onClick={() => handleLetterRead(letter.id)}
                    >
                      Read
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Card>
          </Col>
        ))}

        {/* Person In Charge Widget */}
        <Col xs={24} md={8}>
          <Card
            style={{
              background: '#e6f7ff',
              border: 'none',
              borderRadius: 8,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
              <UserOutlined style={{ fontSize: 24, color: '#1890ff', marginRight: 8 }} />
              <Title level={5} style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
                Person In Charge
              </Title>
            </div>
            <Row gutter={16}>
              {data.staff_roles.map((staff, index) => {
                const colors = ['#1890ff', '#722ed1', '#ff4d4f', '#52c41a', '#fa8c16', '#13c2c2'];
                const color = colors[index % colors.length];
                return (
                  <Col span={12} key={staff.id} style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar
                        style={{
                          backgroundColor: color,
                          marginRight: 8,
                        }}
                      >
                        {staff.initial}
                      </Avatar>
                      <Text style={{ fontSize: 14 }}>{staff.name}</Text>
        </div>
                  </Col>
                );
              })}
            </Row>
      </Card>
        </Col>
      </Row>
    </div>
  );
};
